import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { CREATE_PORT, DELETE_PORT, GET_PORTS, UPDATE_PORT } from '@modules/demand-requirements/ports.graphql';
import { CreatePortResponse, DeletePortResponse, GetPortsResponse, UpdatePortResponse } from '@modules/demand-requirements/types/port';
import { PortsActions } from './ports.actions';

@Injectable()
export class PortsEffects {
    private readonly actions$ = inject(Actions);
    private readonly graphqlService = inject(GraphQlService);
    private readonly swalService = inject(SwalService);

    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortsActions.loadAll),
            switchMap(() =>
                this.graphqlService
                    .query<GetPortsResponse>({
                        query: GET_PORTS,
                        apolloNamespace: ApolloNamespace.registration,
                    })
                    .pipe(
                        map((response) =>
                            PortsActions.loadAllSuccess({
                                ports: response.data?.getPorts?.data ?? [],
                            })
                        ),
                        catchError((error) => of(PortsActions.loadAllFailure({ error })))
                    )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortsActions.create),
            mergeMap(({ input }) =>
                this.graphqlService
                    .mutate<CreatePortResponse>({
                        mutation: CREATE_PORT,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { input },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.createPort;
                            if (res?.data) {
                                this.swalService.success(res.message ?? 'Port created successfully');
                                return PortsActions.createSuccess({ entity: res.data });
                            }
                            return PortsActions.createFailure({ error: res?.message ?? 'Failed to create port' });
                        }),
                        catchError((error) => of(PortsActions.createFailure({ error })))
                    )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortsActions.update),
            mergeMap(({ input }) =>
                this.graphqlService
                    .mutate<UpdatePortResponse>({
                        mutation: UPDATE_PORT,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { input },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.updatePort;
                            if (res?.data) {
                                this.swalService.success(res.message ?? 'Port updated successfully');
                                return PortsActions.updateSuccess({ entity: res.data });
                            }
                            return PortsActions.updateFailure({ error: res?.message ?? 'Failed to update port' });
                        }),
                        catchError((error) => of(PortsActions.updateFailure({ error })))
                    )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PortsActions.delete),
            mergeMap(({ uuid }) =>
                this.graphqlService
                    .mutate<DeletePortResponse>({
                        mutation: DELETE_PORT,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.deletePort;
                            if (res?.status) {
                                this.swalService.success(res.message ?? 'Port deleted successfully');
                                return PortsActions.deleteSuccess({ uuid });
                            }
                            return PortsActions.deleteFailure({ error: res?.message ?? 'Failed to delete port' });
                        }),
                        catchError((error) => of(PortsActions.deleteFailure({ error })))
                    )
            )
        )
    );

    deleteFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PortsActions.deleteFailure),
                tap(({ error }) =>
                    this.swalService.error(typeof error === 'string' ? error : 'Failed to delete port')
                )
            ),
        { dispatch: false }
    );
}
